/**
 * @file api.ts
 * @brief Voice enrollment on the web and desktop app: status, upload, save,
 *        remove (= withdraw consent), consent record.
 *
 * @details
 * The phone's `/api/auth/voice-enrollment/*` routes, unchanged. Upload is the
 * same three steps as the phone (voiceEnrollmentStorage.ts): ask for a signed
 * URL, PUT the recording to GCS, then `complete`. The browser records WebM;
 * the backend stores it as Ogg/Opus so the phone can still play it back
 * (voiceEnrollment.service.js, transcodeWebmSampleToOgg).
 *
 * @references
 * - BACKEND/src/features/auth/routes.js (voice-enrollment routes)
 * - FRONTEND/src/screens/VoiceEnrollment/voiceEnrollmentStorage.ts
 *
 * @author DES
 * @date 04-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, apiPost } from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";

export const VoiceEnrollmentSchema = z
  .object({
    status: z.string().default("none"),
    enrolledAt: z.string().nullable().optional(),
    durationSeconds: z.number().nullable().optional(),
    playbackUrl: z.string().nullable().optional(),
    minDurationSeconds: z.number().optional(),
    maxDurationSeconds: z.number().optional(),
  })
  .passthrough();

export type VoiceEnrollment = z.infer<typeof VoiceEnrollmentSchema>;

const EnrollmentResponseSchema = z.object({ enrollment: VoiceEnrollmentSchema });

const UploadSchema = z.object({
  uploadUrl: z.string(),
  publicUrl: z.string(),
  requiredHeaders: z.record(z.string()).optional(),
});

export const voiceEnrollmentKey = ["auth", "voice-enrollment"] as const;

export function useVoiceEnrollment(enabled = true) {
  return useQuery({
    queryKey: voiceEnrollmentKey,
    enabled,
    queryFn: async () =>
      EnrollmentResponseSchema.parse(await apiGet<unknown>(authEndpoints.voiceEnrollmentStatus))
        .enrollment,
    staleTime: 60_000,
    // One retry, then the page opens on the script rather than spinning.
    retry: 1,
  });
}

export type SaveVoiceSampleInput = {
  blob: Blob;
  contentType: string;
  extension: string;
  durationSeconds: number;
};

export function useSaveVoiceSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ blob, contentType, extension, durationSeconds }: SaveVoiceSampleInput) => {
      const fileName = `voice_enroll_${Date.now()}.${extension}`;
      const signed = UploadSchema.parse(
        await apiPost<unknown>(authEndpoints.voiceEnrollmentRequestUpload, { fileName, contentType }),
      );
      // The PUT's Content-Type must be the one the URL was signed with, or GCS
      // answers 403 SignatureDoesNotMatch (the phone hit this, 02-07-2026).
      const put = await fetch(signed.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType, ...(signed.requiredHeaders ?? {}) },
        body: blob,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status}). Please try again.`);
      return EnrollmentResponseSchema.parse(
        await apiPost<unknown>(authEndpoints.voiceEnrollmentComplete, {
          recordingUrl: signed.publicUrl,
          durationSeconds,
          recordingSizeBytes: blob.size,
          // Required since ADR-046 (19-10-2026): without it the backend
          // answers VOICE_CONSENT_REQUIRED. The version names the wording the
          // backend records (VOICE_CONSENT_TEXTS); bump both sides together.
          consent: true,
          consentVersion: VOICE_CONSENT_VERSION,
        }),
      ).enrollment;
    },
    onSuccess: (enrollment) => {
      qc.setQueryData(voiceEnrollmentKey, enrollment);
      void qc.invalidateQueries({ queryKey: voiceConsentKey });
    },
  });
}

/** The consent wording this page shows (BACKEND voiceprintConsent.mjs). */
export const VOICE_CONSENT_VERSION = "2026-10-04";

/** "Remove my voice and withdraw consent" (ADR-046): deletes the voiceprint
 *  and every sample now, and records the withdrawal. */
export function useRemoveVoiceSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      EnrollmentResponseSchema.parse(await apiPost<unknown>(authEndpoints.voiceConsentWithdraw))
        .enrollment,
    onSuccess: (enrollment) => {
      qc.setQueryData(voiceEnrollmentKey, enrollment);
      void qc.invalidateQueries({ queryKey: voiceConsentKey });
    },
  });
}

const VoiceConsentSchema = z
  .object({
    current: z
      .object({
        given: z.boolean().default(false),
        version: z.string().nullable().optional(),
        at: z.string().nullable().optional(),
      })
      .passthrough(),
  })
  .passthrough();

export const voiceConsentKey = ["auth", "voice-consent"] as const;

/** For the "Consent given on <date>" line under a registered voice. */
export function useVoiceConsent(enabled = true) {
  return useQuery({
    queryKey: voiceConsentKey,
    enabled,
    queryFn: async () =>
      VoiceConsentSchema.parse(await apiGet<unknown>(authEndpoints.voiceConsent)).current,
    staleTime: 60_000,
    retry: 1,
  });
}
